/**
 * TraversalSessionStore — observables and setters that represent auxiliary
 * traversal state (widget state, UI flags, tracking metadata, etc.) but do
 * NOT directly drive the core `data: Data` payload or API calls.
 *
 * Separated from TraversalStore so the core store can focus purely on
 * `traversalId`, `data`, and the API / form-field actions.
 */
import { action, observable, makeObservable } from 'mobx'
import { AxiosResponse } from 'axios'
import { FintecState } from '@finanzcheck/csi-tink-widget'
import { BrandTracking } from 'texts/common/lists/brand_tracking'
import { PayoutTime } from 'texts/common/lists/payout_time'

class TraversalSessionStore {
  // -------------------------------------------------------------------------
  // MobX observables
  // -------------------------------------------------------------------------

  /** Used in AddSecondDebtor page (left-over from CAT-1399) */
  public hasInteractedWithDebtorsCount = false
  /** Used in AddSecondDebtor page (left-over from CAT-1399) */
  public isDebtorsCountError = false

  public isGoToFinalSucceeded = false

  public dacWidgetState = 'initial' as FintecState
  public dacWidgetStep = ''
  public isDacSkipped = false

  public calculatedManuallyAdaptedAmount = 0

  public isVoucherActivated = false

  // -------------------------------------------------------------------------
  // Non-observable public state
  // -------------------------------------------------------------------------

  public nextResumeHash?: string
  public resumeData: AxiosResponse | Record<string, string> = {}
  public touchedFields: string[] = []
  public brandTracking: BrandTracking | '' = ''
  public desiredPayoutTime: PayoutTime | 'none' = 'none'
  public initialPaymentErrorTriggered = false

  /** Remove after cleanup of CAT-4118 */
  public rceEditInitialData: {
    street?: string
    houseNumber?: string
    iban?: string
    amount?: number
    income?: number
    pension?: number
  } = {}

  constructor() {
    makeObservable(this, {
      hasInteractedWithDebtorsCount: observable,
      isDebtorsCountError: observable,
      isGoToFinalSucceeded: observable,
      dacWidgetState: observable,
      dacWidgetStep: observable,
      isDacSkipped: observable,
      calculatedManuallyAdaptedAmount: observable,
      setHasInteractedWithDebtorsCount: action,
      setIsDebtorsCountError: action,
      setIsGoToFinalSucceeded: action,
      setDacWidgetState: action,
      setDacWidgetStep: action,
      setIsDacSkipped: action,
      setCalculatedManuallyAdaptedAmount: action,
      isVoucherActivated: observable,
      setIsVoucherActivated: action,
      rceEditInitialData: observable,
      setRceEditInitialData: action
    })
  }

  // -------------------------------------------------------------------------
  // Setters for MobX observables
  // -------------------------------------------------------------------------

  setHasInteractedWithDebtorsCount = (value: boolean) => {
    this.hasInteractedWithDebtorsCount = value
  }

  setIsDebtorsCountError = (value: boolean) => {
    this.isDebtorsCountError = value
  }

  setIsGoToFinalSucceeded = (value: boolean) => {
    this.isGoToFinalSucceeded = value
  }

  setDacWidgetState = (dacWidgetState: FintecState) => {
    this.dacWidgetState = dacWidgetState
  }

  setDacWidgetStep = (dacWidgetStep: string) => {
    this.dacWidgetStep = dacWidgetStep
  }

  setIsDacSkipped = (isDacSkipped: boolean) => {
    this.isDacSkipped = isDacSkipped
  }

  setCalculatedManuallyAdaptedAmount = (amount: number) => {
    this.calculatedManuallyAdaptedAmount = amount
  }

  setIsVoucherActivated = (value: boolean) => {
    this.isVoucherActivated = value
  }

  // -------------------------------------------------------------------------
  // Setters for non-observable state
  // -------------------------------------------------------------------------

  setTouchedFields = (path: string) => {
    if (this.touchedFields.includes(path)) {
      return
    }
    this.touchedFields.push(path)
  }

  setBrandTracking = (brandTracking: BrandTracking | '') => {
    this.brandTracking = brandTracking
  }

  setDesiredPayoutTime = (desiredPayoutTime: PayoutTime | 'none') => {
    this.desiredPayoutTime = desiredPayoutTime
  }

  setInitialPaymentErrorTriggered = (value: boolean) => {
    this.initialPaymentErrorTriggered = value
  }

  setRceEditInitialData = (
    editedData: Partial<{
      street: string
      houseNumber: string
      iban: string
      amount: number
      income: number
      pension: number
    }>
  ) => {
    this.rceEditInitialData = {
      ...this.rceEditInitialData,
      ...editedData
    }
  }
}

export default TraversalSessionStore
